"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const design_system_1 = require("@zapier/design-system");
const common_utils_1 = require("@zapier/common-utils");
const NavDropdown_1 = __importDefault(require("./NavDropdown"));
const getNavItemAriaLabel_1 = __importDefault(require("./getNavItemAriaLabel"));
const indicatorTrackHeight = 3;
const Styles = common_utils_1.labelStyles(__filename, {
    root: (props) => [
        {
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            columnGap: 20,
            rowGap: 2,
            alignItems: 'center',
        },
        props.size === 'medium' &&
            common_utils_1.withModifier('medium', {
                height: 50,
                fontSize: 14,
            }),
        props.size === 'large' &&
            common_utils_1.withModifier('large', {
                height: 70,
                fontSize: 16,
            }),
    ],
    primary: ({ count }) => [
        {
            display: 'grid',
            height: '100%',
            minWidth: 0,
            maxWidth: '100%',
        },
        common_utils_1.withModifier(`count-${count}`, {
            gridTemplateColumns: `repeat(${count}, minmax(auto, min-content))`,
            gap: 20,
        }),
    ],
    item: ({ isDisabled, isSelected, }) => [
        {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 10px',
            textAlign: 'center',
            color: design_system_1.Colors.grey700,
            lineHeight: '20px',
            fontWeight: 600,
            '&:hover, &:focus': {
                color: design_system_1.Colors.blue500,
            },
        },
        isSelected &&
            common_utils_1.withModifier('selected', {
                color: design_system_1.Colors.blue500,
            }),
        isDisabled &&
            common_utils_1.withModifier('disabled', {
                cursor: 'not-allowed',
                color: design_system_1.Colors.grey700,
            }),
    ],
    itemText: (itemMaxWidth) => (Object.assign({ display: 'block', whiteSpace: 'nowrap' }, (itemMaxWidth
        ? {
            maxWidth: itemMaxWidth,
            overflowX: 'hidden',
            textOverflow: 'ellipsis',
        }
        : {}))),
    indicatorTrack: (props) => [
        {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            borderRadius: indicatorTrackHeight / 2,
            height: indicatorTrackHeight,
            overflow: 'hidden',
        },
        props.shouldRenderTrack &&
            common_utils_1.withModifier('visible', {
                backgroundColor: design_system_1.Colors.grey100,
            }),
    ],
    indicator: {
        position: 'absolute',
        height: 'inherit',
        borderRadius: 'inherit',
        backgroundColor: design_system_1.Colors.blue500,
        transition: 'all 250ms ease-in-out',
    },
});
const NavItem = (props) => {
    const ariaLabel = getNavItemAriaLabel_1.default(props);
    const itemTextStyle = Styles.itemText(props.itemMaxWidth);
    if (props.isDisabled) {
        return (common_utils_1.isolateComponent("div", { "aria-label": ariaLabel, css: Styles.item(props) },
            common_utils_1.isolateComponent("span", { css: itemTextStyle }, props.text)));
    }
    return (common_utils_1.isolateComponent(design_system_1.Link, { ariaLabel: ariaLabel, color: null, css: Styles.item(props), href: props.href, onClick: props.onChoose, ref: props.forwardedRef },
        common_utils_1.isolateComponent("span", { css: itemTextStyle }, props.text)));
};
const Nav = (props) => {
    const selectedItemRef = react_1.useRef(null);
    const [indicatorStyles, setIndicatorStyles] = react_1.useState({});
    const shouldRenderIndicator = !props.items.every(i => i.isExtra);
    const primaryItems = props.items.filter(item => !item.isExtra);
    const primaryLinks = primaryItems.map(item => {
        const isSelected = props.isItemSelected(item, props.selectedItemHref);
        return (common_utils_1.isolateComponent(NavItem, Object.assign({}, item, { forwardedRef: isSelected && shouldRenderIndicator ? selectedItemRef : null, isSelected: isSelected, itemMaxWidth: props.itemMaxWidth, key: item.text, onChoose: event => props.onChooseItem(item, event) })));
    });
    const secondaryItems = props.items.filter(item => item.isExtra);
    const isSecondaryItemSelected = secondaryItems.some(item => {
        return props.isItemSelected(item, props.selectedItemHref);
    });
    const secondaryMenu = secondaryItems.length > 0 && (common_utils_1.isolateComponent("div", { ref: isSecondaryItemSelected && shouldRenderIndicator
            ? selectedItemRef
            : null },
        common_utils_1.isolateComponent(NavDropdown_1.default, { items: secondaryItems, onChooseItem: props.onChooseItem, selectedItemHref: props.selectedItemHref, shouldIndicateSelected: shouldRenderIndicator, size: props.size })));
    react_1.useEffect(() => {
        requestAnimationFrame(() => {
            var _a, _b;
            setIndicatorStyles(selectedItemRef.current
                ? {
                    left: (_a = selectedItemRef === null || selectedItemRef === void 0 ? void 0 : selectedItemRef.current) === null || _a === void 0 ? void 0 : _a.offsetLeft,
                    width: (_b = selectedItemRef === null || selectedItemRef === void 0 ? void 0 : selectedItemRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth,
                }
                : {});
        });
    }, [props.items, props.selectedItemHref]);
    return (common_utils_1.isolateComponent("nav", { "aria-label": props.ariaLabel, css: Styles.root(props) },
        common_utils_1.isolateComponent("div", { css: Styles.primary({ count: primaryLinks.length }), ref: props.forwardedRef }, primaryLinks),
        secondaryMenu,
        common_utils_1.isolateComponent("div", { css: Styles.indicatorTrack(props) },
            common_utils_1.isolateComponent("div", { css: Styles.indicator, style: indicatorStyles }))));
};
Nav.defaultProps = {
    isDisabled: false,
    isItemSelected: (item, selectedItemHref) => item.href === selectedItemHref,
    onChooseItem: () => { },
    shouldRenderTrack: true,
    size: 'medium',
};
exports.default = Nav;
